/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useMemo, useState } from 'react';

import { captureException } from '@sentry/react';

import { isPoweruser } from 'js/lib/user';

import { Button, Checkbox, Dialog, LoadingSection, Search } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useEnableLearnerGroups } from 'bundles/enterprise-admin-hooks/hooks/useEnableLearnerGroups';
import { useEnterpriseProgramsByOrganizationSlug } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseProgramsByOrganizationSlug';
import {
  useExitPreviewMode,
  usePreviewProgramIds,
  useSetPreviewMode,
} from 'bundles/unified-common/contexts/org-scoped/OrgScopedImpersonationContext';
import {
  useOrgSlug,
  useThirdPartyOrganization,
} from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import { useEnterpriseOrgScopedModalManager } from 'bundles/unified-common/providers/org-scoped/modals/EnterpriseOrgScopedModalManagerContext';

import _t from 'i18n!nls/unified-home-common';

const { HeadingGroup, Content, Actions } = Dialog;

// Fetch the full set of permitted programs in one page.
// Matches the PROGRAMS_FETCH_LIMIT used by other "list all programs" admin consumers.
const PROGRAMS_FETCH_LIMIT = 5000;

const styles = {
  contentInner: css`
    display: flex;
    flex-direction: column;
    height: 480px;
  `,
  filterInput: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-300);
  `,
  programList: (columns: number) => css`
    display: grid;
    grid-template-columns: repeat(${columns}, 1fr);
    gap: var(--cds-spacing-200);
    align-content: start;
    flex: 1;
    overflow-y: auto;
  `,
  actions: css`
    display: flex;
    gap: var(--cds-spacing-200);
    align-items: center;
  `,
};

const OrgScopedChangePreviewProgramsModal = () => {
  const modalManager = useEnterpriseOrgScopedModalManager();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const enableLearnerGroups = useEnableLearnerGroups(thirdPartyOrganization?.id);
  const orgSlug = useOrgSlug();
  // Power users can preview any program in the org (backend-authorized independent of
  // program-level VIEW permission), so skip the permission filter for them.
  const { programs, loading } = useEnterpriseProgramsByOrganizationSlug(
    orgSlug ?? '',
    undefined,
    undefined,
    PROGRAMS_FETCH_LIMIT,
    undefined,
    isPoweruser()
  );
  const currentPreviewProgramIds = usePreviewProgramIds();
  const setPreviewMode = useSetPreviewMode();
  const exitPreviewMode = useExitPreviewMode();
  const track = useTracker();

  const [filterText, setFilterText] = useState('');
  const [selectedProgramIds, setSelectedProgramIds] = useState<Set<string>>(new Set(currentPreviewProgramIds));

  const filteredPrograms = useMemo(
    () => (programs ?? []).filter((p) => p.name.toLowerCase().includes(filterText.toLowerCase())),
    [programs, filterText]
  );

  const columnCount = filteredPrograms.length > 20 ? 3 : 2;

  const handleClose = () => {
    modalManager?.setShowChangePreviewProgramsModal(false);
  };

  const handleToggle = useCallback<(programId: string) => void>((programId) => {
    setSelectedProgramIds((prev) => {
      const next = new Set(prev);
      if (next.has(programId)) {
        next.delete(programId);
      } else {
        next.add(programId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedProgramIds(new Set((programs ?? []).map((p) => p.id)));
  }, [programs]);

  const handleDeselectAll = useCallback(() => {
    setSelectedProgramIds(new Set());
  }, []);

  const handleApply = async () => {
    try {
      // Applying an empty selection means there is nothing left to impersonate,
      // so end the preview session entirely instead of previewing zero programs.
      // (exitPreviewMode fires its own `enterprise_admin_exit_preview` click, so
      // this branch doesn't need its own event.)
      if (selectedProgramIds.size === 0) {
        await exitPreviewMode?.();
      } else {
        track('click_button', {
          button: { name: 'enterprise_admin_preview_change_group_confirm' },
          enterprise: { organizationSlug: orgSlug },
        });
        await setPreviewMode?.(Array.from(selectedProgramIds));
      }
    } catch (err) {
      captureException(err, { extra: { message: 'Error applying admin preview program selection' } });
    } finally {
      handleClose();
    }
  };

  return (
    <Dialog
      aria-describedby="change-preview-programs-dialog-content"
      open
      width="large"
      onClose={handleClose}
      data-testid="change-preview-programs-dialog"
    >
      <HeadingGroup>
        {enableLearnerGroups ? _t('Change groups you want to preview') : _t('Change programs you want to preview')}
      </HeadingGroup>
      <Content id="change-preview-programs-dialog-content">
        <div css={styles.contentInner}>
          <Search
            css={styles.filterInput}
            fullWidth
            placeholder={_t('Filter results')}
            hideSearchButton
            value={filterText}
            onChange={setFilterText}
            onClear={() => setFilterText('')}
            data-testid="change-preview-programs-search"
          />
          {loading ? (
            <LoadingSection label={enableLearnerGroups ? _t('Loading groups') : _t('Loading programs')} />
          ) : (
            <div css={styles.programList(columnCount)}>
              {filteredPrograms.map((program) => (
                <Checkbox
                  key={program.id}
                  label={program.name}
                  checked={selectedProgramIds.has(program.id)}
                  onChange={() => handleToggle(program.id)}
                />
              ))}
            </div>
          )}
        </div>
      </Content>
      <Actions>
        <div css={styles.actions}>
          <Button variant="primary" onClick={handleApply} data-testid="change-preview-programs-apply">
            {_t('Apply')}
          </Button>
          <Button variant="ghost" onClick={handleSelectAll} data-testid="change-preview-programs-select-all">
            {_t('Select all')}
          </Button>
          <Button variant="ghost" onClick={handleDeselectAll} data-testid="change-preview-programs-deselect-all">
            {_t('Deselect all')}
          </Button>
        </div>
      </Actions>
    </Dialog>
  );
};

export default OrgScopedChangePreviewProgramsModal;
